// Pure parts of the filter panel (port of MapFilterSheet.swift): the day-range chips, the territory list a viewer may
// pick from (Territories+Store.visibleTerritories) and small edits on a MapFilter draft. The filtering itself is
// domain/records.ts `applyFilter`; nothing here decides which store matches.

import type { RecordType } from '@/contracts/enums';
import type { SettingsPayload, TerritoryPayload } from '@/contracts/models';
import { sameId } from '@/contracts/uuid';
import type { MapFilter } from '@/domain/records';
import { compareLocalized } from '@/domain/util';
import type { Viewer } from '@/domain/visibility';

/** `MapFilterSheet.DaysPreset`: Any, 0–7, 8–30, 31–90, 90+ (inclusive bounds; 90+ is "at least 91"). */
export const DAYS_PRESETS = [
  { id: 'any', title: 'Any', min: undefined, max: undefined },
  { id: 'week', title: '0–7', min: 0, max: 7 },
  { id: 'month', title: '8–30', min: 8, max: 30 },
  { id: 'quarter', title: '31–90', min: 31, max: 90 },
  { id: 'older', title: '90+', min: 91, max: undefined },
] as const satisfies readonly { id: string; title: string; min: number | undefined; max: number | undefined }[];
export type DaysPreset = (typeof DAYS_PRESETS)[number];

export type DaysRangeKind = 'visit' | 'activity';

/** The bounds of one range (`daysSinceVisitMin/Max` or `daysSinceActivityMin/Max`). */
export function daysRange(f: MapFilter, kind: DaysRangeKind): { min: number | undefined; max: number | undefined } {
  return kind === 'visit'
    ? { min: f.daysSinceVisitMin, max: f.daysSinceVisitMax }
    : { min: f.daysSinceActivityMin, max: f.daysSinceActivityMax };
}

/** Sets one range; undefined clears that bound. */
export function withDaysRange(f: MapFilter, kind: DaysRangeKind, min: number | undefined, max: number | undefined): MapFilter {
  const next: MapFilter = { ...f };
  const [minKey, maxKey] =
    kind === 'visit' ? (['daysSinceVisitMin', 'daysSinceVisitMax'] as const) : (['daysSinceActivityMin', 'daysSinceActivityMax'] as const);
  if (min === undefined) delete next[minKey];
  else next[minKey] = min;
  if (max === undefined) delete next[maxKey];
  else next[maxKey] = max;
  return next;
}

/** A chip is on when both bounds equal the preset's (Any = no bounds). */
export function isDaysPresetOn(f: MapFilter, kind: DaysRangeKind, preset: DaysPreset): boolean {
  const r = daysRange(f, kind);
  return r.min === preset.min && r.max === preset.max;
}

/** The "At least" / "At most" field text → a bound: whole days 0…3650 (the iOS stepper's range); empty = Any. */
export function parseDayBound(text: string): number | undefined | 'invalid' {
  const t = text.trim();
  if (t === '') return undefined;
  if (!/^\d{1,4}$/.test(t)) return 'invalid';
  const n = Number(t);
  return n <= 3650 ? n : 'invalid';
}

/** Adds or removes a value from one of the filter's lists (ids compare case-insensitively). */
export function toggleIn<T extends string>(list: readonly T[], value: T, ids = false): T[] {
  const has = ids ? list.some((v) => sameId(v, value)) : list.includes(value);
  if (has) return list.filter((v) => (ids ? !sameId(v, value) : v !== value));
  return [...list, value];
}

export function hasIn(list: readonly string[], value: string, ids = false): boolean {
  return ids ? list.some((v) => sameId(v, value)) : list.includes(value);
}

/** Toggles a record type (RecordType chips). */
export function toggleType(f: MapFilter, type: RecordType): MapFilter {
  return { ...f, types: toggleIn(f.types, type) };
}

/**
 * Territories a viewer may filter by, by name: managers see all; reps the ones they are assigned to, plus
 * unassigned ones when reps may create territories (Territories+Store.visibleTerritories).
 */
export function filterableTerritories(
  territories: readonly TerritoryPayload[],
  viewer: Viewer | null,
  settings: Pick<SettingsPayload, 'allowRepsToCreateTerritories'>,
): TerritoryPayload[] {
  const sorted = [...territories].sort((a, b) => compareLocalized(a.name, b.name));
  if (!viewer || viewer.role !== 'rep' || !viewer.profileId) return sorted;
  const me = viewer.profileId;
  return sorted.filter(
    (t) => t.assignedUserIds.some((u) => sameId(u, me)) || (settings.allowRepsToCreateTerritories && t.assignedUserIds.length === 0),
  );
}
